import React, { useEffect, useState, useMemo, useRef } from "react";
import { connect } from "react-redux";
import "./FolderList.css";
import { EditOutlined, DeleteOutlined, DownOutlined } from "@ant-design/icons";
import DataHelper from "../../helpers/DataHelper";
import NewFolderButton from "../NewFolderButton";
import useFolderFileStore from "../../store/folderFileStore";
import {
  requestServerGet,
  requestServerPost,
  selectBrand,
  selectPage,
} from "../../actions";
import { Modal, Select, Tree } from "antd";
import useFileStore from "../../store/fileStore";
import { IoMdFolder } from "react-icons/io";
import { MdFolderShared, MdFolderSpecial } from "react-icons/md";
import DroppableWrapper from "../../dnd/DroppableWrapper";

const FolderList = (props) => {
  const { folders = [], compact = false } = props;

  const folderFileDatas = useFolderFileStore((state) => state.folderFileDatas);
  const fetchFolderFileDatas = useFolderFileStore(
    (state) => state.fetchFolderFileDatas,
  );
  const selectedFolderID = useFolderFileStore(
    (state) => state.selectedFolderID,
  );
  const setSelectedFolderID = useFolderFileStore(
    (state) => state.setSelectedFolderID,
  );
  const mediaDatas = useFileStore((state) => state.mediaDatas);

  const { selectedBrandData } = props;
  const isSharingMode2 = selectedBrandData?.ContentSharingMode == "2";

  const [branches, setBranches] = useState([]);
  const [expandedKeys, setExpandedKeys] = useState([]);

  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [createName, setCreateName] = useState("");
  const [createAccessBranches, setCreateAccessBranches] = useState(["all"]);

  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editModalFolder, setEditModalFolder] = useState(null);
  const [editModalName, setEditModalName] = useState("");
  const [editModalAccessBranches, setEditModalAccessBranches] = useState([
    "all",
  ]);

  useEffect(() => {
    if (isSharingMode2 && selectedBrandData) {
      DataHelper.getBranches(
        { brandid: selectedBrandData.BrandID },
        props,
      ).then((data) => setBranches(data || []));
    }
  }, [selectedBrandData?.BrandID]);

  // Auto-expand ancestors when selected folder changes
  useEffect(() => {
    if (!selectedFolderID) return;
    const ancestors = [];
    let current = folders.find(
      (f) => String(f.FolderID) === String(selectedFolderID),
    );
    while (current && current.FolderParentID) {
      ancestors.push(String(current.FolderParentID));
      current = folders.find(
        (f) => String(f.FolderID) === String(current.FolderParentID),
      );
    }
    setExpandedKeys((prev) => [...new Set([...prev, ...ancestors])]);
  }, [selectedFolderID, folders]);

  const getAllowedBranchesForFolder = (folderID) => {
    if (!folderID) return { branches, isRestricted: false };
    const folder = folderFileDatas.find(
      (f) => String(f.FolderID) === String(folderID),
    );
    if (!folder) return { branches, isRestricted: false };
    const raw = folder.AccessBranches;
    const isAll = !raw || raw === "null" || raw === "all" || raw === "";
    if (isAll) return { branches, isRestricted: false };
    const allowedIds = new Set(
      String(raw)
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean),
    );
    return {
      branches: branches.filter((b) => allowedIds.has(String(b.BranchID))),
      isRestricted: true,
      defaultSelection: [...allowedIds],
    };
  };

  const handleBranchesChange = (value, current, setter) => {
    if (value.includes("all") && !current.includes("all")) setter(["all"]);
    else if (current.includes("all") && value.length > 1)
      setter(value.filter((v) => v !== "all"));
    else if (value.length === 0) setter(["all"]);
    else setter(value);
  };

  const renderBranchSelector = (
    value,
    onChange,
    availableBranches = branches,
    isParentRestricted = false,
  ) => {
    if (!isSharingMode2 || branches.length === 0 || !!props.selectedBranchData)
      return null;
    // Deduplicate by BranchID — the API sometimes returns duplicate records
    const seen = new Set();
    const uniqueBranches = availableBranches.filter((b) => {
      const id = String(b.BranchID);
      if (seen.has(id)) return false;
      seen.add(id);
      return true;
    });
    return (
      <div
        style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 12 }}
      >
        <label style={{ flexShrink: 0, fontWeight: 500, color: "#666" }}>
          Accessible Branches
        </label>
        <Select
          mode="multiple"
          style={{ flex: 1, minWidth: 0 }}
          value={value}
          onChange={onChange}
          maxTagCount="responsive"
        >
          {!isParentRestricted && (
            <Select.Option key="all" value="all">
              All Branches
            </Select.Option>
          )}
          {uniqueBranches.map((b) => (
            <Select.Option key={b.BranchID} value={String(b.BranchID)}>
              {b.BranchName}
            </Select.Option>
          ))}
        </Select>
      </div>
    );
  };

  const handleDeleteFolder = (folder) => {
    Modal.confirm({
      title: "Delete Folder",
      content: `Are you sure you want to delete folder "${folder.FolderName}"?`,
      okText: "Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        await DataHelper.deleteFolderFile({ folderid: folder.FolderID }, props);
        await fetchFolderFileDatas(props.selectedBrandData, props);
      },
    });
  };

  const handleEditClick = (folder) => {
    const currentBranches =
      !folder.AccessBranches ||
      folder.AccessBranches === "null" ||
      folder.AccessBranches === "all" ||
      folder.AccessBranches === ""
        ? ["all"]
        : String(folder.AccessBranches)
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean);
    setEditModalFolder(folder);
    setEditModalName(folder.FolderName);
    setEditModalAccessBranches(currentBranches);
    setEditModalVisible(true);
  };

  const createNewFolder = () => {
    const parentInfo = getAllowedBranchesForFolder(selectedFolderID);
    setCreateName("");
    setCreateAccessBranches(
      parentInfo.isRestricted ? parentInfo.defaultSelection : ["all"],
    );
    setCreateModalVisible(true);
  };

  const handleCreateSubmit = async () => {
    if (!createName.trim()) return;
    const isBranchLevel = isSharingMode2 && !!props.selectedBranchData;
    const override =
      isSharingMode2 && !isBranchLevel
        ? createAccessBranches.includes("all")
          ? "all"
          : createAccessBranches.join(",")
        : null;
    await DataHelper.createFolderFile(
      createName.trim(),
      selectedFolderID,
      props,
      override,
    );
    await fetchFolderFileDatas(props.selectedBrandData, props);
    setCreateModalVisible(false);
  };

  const handleEditSubmit = async () => {
    if (!editModalName.trim() || !editModalFolder) return;
    const isBranchLevel = isSharingMode2 && !!props.selectedBranchData;
    let override = null;
    if (isSharingMode2) {
      override = isBranchLevel
        ? String(props.selectedBranchData.BranchID)
        : editModalAccessBranches.includes("all")
          ? "all"
          : editModalAccessBranches.join(",");
    }
    await DataHelper.updateFolderFile(
      { folderid: editModalFolder.FolderID, foldername: editModalName.trim() },
      props,
      override,
    );
    await fetchFolderFileDatas(props.selectedBrandData, props);
    setEditModalVisible(false);
    setEditModalFolder(null);
  };

  const folderFileCountMap = useMemo(() => {
    const map = {};
    mediaDatas.forEach((file) => {
      const fid = file.FolderParentID;
      if (fid != null && fid !== "") {
        const key = String(fid);
        map[key] = (map[key] || 0) + 1;
      }
    });
    return map;
  }, [mediaDatas]);

  const buildTreeData = (allFolders, parentID = null) => {
    return allFolders
      .filter((f) => {
        const pid = f.FolderParentID;
        if (parentID === null)
          return (
            pid === null ||
            pid === undefined ||
            pid === "" ||
            pid === "null" ||
            pid === 0
          );
        return String(pid) === String(parentID);
      })
      .sort((a, b) => a.FolderName.localeCompare(b.FolderName))
      .map((folder) => ({
        key: String(folder.FolderID),
        folderData: folder,
        children: buildTreeData(allFolders, folder.FolderID),
      }));
  };

  const treeData = useMemo(() => buildTreeData(folders), [folders]);

  // Compact mode scrolls the folder list; show a "more below" hint (fade +
  // chevron) whenever the bottom sentinel isn't visible, and hide it once
  // the user has scrolled it into view.
  const scrollRef = useRef(null);
  const bottomSentinelRef = useRef(null);
  const [canScrollMore, setCanScrollMore] = useState(false);

  useEffect(() => {
    if (!compact) return;
    const root = scrollRef.current;
    const sentinel = bottomSentinelRef.current;
    if (!root || !sentinel) return;
    const observer = new IntersectionObserver(
      ([entry]) => setCanScrollMore(!entry.isIntersecting),
      { root, threshold: 1 },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [compact, treeData, expandedKeys]);

  // titleRender is called fresh on every render so closures are always current
  const titleRender = (nodeData) => {
    const folder = nodeData.folderData;
    const fid = String(folder.FolderID);
    const count = folderFileCountMap[fid];
    const isBrandLevelFolder = isSharingMode2 && folder.CreateLevel < 2;
    const isRestrictedAccess =
      isSharingMode2 &&
      folder.AccessBranches &&
      folder.AccessBranches !== "null" &&
      folder.AccessBranches !== "all" &&
      folder.AccessBranches !== "";

    const folderIcon = !isBrandLevelFolder ? (
      <IoMdFolder
        style={{ fontSize: 14, color: "#f5a623", verticalAlign: "middle" }}
      />
    ) : isRestrictedAccess ? (
      <MdFolderSpecial
        style={{ fontSize: 14, color: "#6b9fd4", verticalAlign: "middle" }}
      />
    ) : (
      <MdFolderShared
        style={{ fontSize: 14, color: "#6b9fd4", verticalAlign: "middle" }}
      />
    );

    return (
      <DroppableWrapper
        id={`folder_${fid}`}
        data={{
          type: "folder",
          id: folder.FolderID,
          name: folder.FolderName,
        }}
      >
        <div className="folder-tree-node">
          <span style={{ marginRight: 5, lineHeight: 1 }}>{folderIcon}</span>
          <span className="folder-tree-label">
            {folder.FolderName}
            {count > 0 && (
              <span className="folder-tree-count">&nbsp;({count})</span>
            )}
          </span>
          <span
            className="folder-tree-actions"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="folder-tree-action-btn"
              onClick={(e) => {
                e.stopPropagation();
                handleEditClick(folder);
              }}
              title="Rename"
            >
              <EditOutlined style={{ fontSize: 11 }} />
            </button>
            <button
              className="folder-tree-action-btn folder-tree-action-btn--danger"
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteFolder(folder);
              }}
              title="Delete"
            >
              <DeleteOutlined style={{ fontSize: 11 }} />
            </button>
          </span>
        </div>
      </DroppableWrapper>
    );
  };

  const selectedKeys = selectedFolderID ? [String(selectedFolderID)] : [];

  return (
    <div style={{ width: "100%" }}>
      <Modal
        title="New Folder"
        open={createModalVisible}
        onOk={handleCreateSubmit}
        onCancel={() => setCreateModalVisible(false)}
        okText="Create"
        okButtonProps={{ disabled: !createName.trim() }}
      >
        <input
          autoFocus
          placeholder="Folder name"
          value={createName}
          onChange={(e) => setCreateName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") handleCreateSubmit();
          }}
          style={{
            width: "100%",
            padding: "6px 10px",
            border: "1px solid #d9d9d9",
            borderRadius: 4,
            fontSize: 14,
            outline: "none",
          }}
        />
        {(() => {
          const p = getAllowedBranchesForFolder(selectedFolderID);
          return renderBranchSelector(
            createAccessBranches,
            (val) =>
              handleBranchesChange(
                val,
                createAccessBranches,
                setCreateAccessBranches,
              ),
            p.branches,
            p.isRestricted,
          );
        })()}
      </Modal>

      <Modal
        title="Edit Folder"
        open={editModalVisible}
        onOk={handleEditSubmit}
        onCancel={() => {
          setEditModalVisible(false);
          setEditModalFolder(null);
        }}
        okText="Save"
        okButtonProps={{ disabled: !editModalName.trim() }}
      >
        <input
          autoFocus
          placeholder="Folder name"
          value={editModalName}
          onChange={(e) => setEditModalName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") handleEditSubmit();
          }}
          style={{
            width: "100%",
            padding: "6px 10px",
            border: "1px solid #d9d9d9",
            borderRadius: 4,
            fontSize: 14,
            outline: "none",
          }}
        />
        {(() => {
          const p = getAllowedBranchesForFolder(
            editModalFolder?.FolderParentID,
          );
          return renderBranchSelector(
            editModalAccessBranches,
            (val) =>
              handleBranchesChange(
                val,
                editModalAccessBranches,
                setEditModalAccessBranches,
              ),
            p.branches,
            p.isRestricted,
          );
        })()}
      </Modal>

      <NewFolderButton createNewFolder={createNewFolder} />

      <div style={{ position: "relative" }}>
        <div
          ref={compact ? scrollRef : null}
          style={
            compact ? { maxHeight: 190, overflowY: "auto" } : undefined
          }
        >
          <DroppableWrapper
            id="folder_root"
            data={{
              type: "breadcrumb",
              id: null,
              name: "Root",
            }}
          >
            <div
              className={`folder-row folder-row--root${!selectedFolderID ? " folder-row--selected" : ""}`}
              onClick={() => setSelectedFolderID(null)}
            >
              <span style={{ fontSize: 13 }}>Root</span>
            </div>
          </DroppableWrapper>

          {treeData.length > 0 && (
            <Tree
              blockNode
              showLine={{ showLeafIcon: false }}
              selectedKeys={selectedKeys}
              expandedKeys={expandedKeys}
              onExpand={(keys) => setExpandedKeys(keys)}
              onSelect={(keys, { node }) => {
                if (keys.length > 0)
                  setSelectedFolderID(node.folderData.FolderID);
              }}
              treeData={treeData}
              titleRender={titleRender}
              style={{ background: "transparent", fontSize: 13 }}
            />
          )}
          {compact && <div ref={bottomSentinelRef} style={{ height: 1 }} />}
        </div>

        {compact && canScrollMore && (
          <div
            style={{
              position: "absolute",
              left: 0,
              right: 0,
              bottom: 0,
              height: 30,
              display: "flex",
              alignItems: "flex-end",
              justifyContent: "center",
              paddingBottom: 2,
              background:
                "linear-gradient(to bottom, rgba(255,255,255,0), #fff 75%)",
              pointerEvents: "none",
            }}
          >
            <DownOutlined style={{ fontSize: 11, color: "#94a3b8" }} />
          </div>
        )}
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  userdata: state.userdata,
  usertoken: state.usertoken,
  globalSearch: state.globalSearch,
  selectedBrandData: state.selectedBrandData,
  selectedBranchData: state.selectedBranchData,
});

export default connect(mapStateToProps, {
  requestServerGet,
  requestServerPost,
  selectBrand,
  selectPage,
})(FolderList);
